import { useState, useMemo, useEffect, useRef, useCallback, memo } from 'react';
import { useResponsive } from '../../hooks/useResponsive';
import { VariableSizeList } from 'react-window';
import OptimizedMemberCard from './OptimizedMemberCard';
import LoadingSpinner from '../common/LoadingSpinner';
import { useVirtualizedList, useOptimizedHover, useOptimizedFavorites } from '../../hooks/useVirtualizedList';

const DEFAULT_CARD_HEIGHT = 400; // Default height estimate for cards (increased for wider screens)
const DEFAULT_COMPACT_HEIGHT = 115; // Default height estimate for compact view
const OVERSCAN_COUNT = 5; // Number of items to render outside visible area

// ── Stable top-level components for react-window ──
// Defined outside VirtualizedMemberList so their component identity never changes.
// This prevents react-window from unmounting/remounting rows on every state change,
// which was causing LazyCoverImage/LazyAvatar to lose loaded state and reload images.

const MeasuredCompactCard = memo(function MeasuredCompactCard({ index, member, viewMode, isHighlighted, style, data }) {
    const { config, handleMemberClick, checkIsFavorite, handleToggleFavorite, checkIsPending, isLoggedIn, setItemHeight } = data;
    const measureRef = useRef(null);

    useEffect(() => {
        if (measureRef.current) {
            const height = measureRef.current.scrollHeight;
            setItemHeight(index, height);
        }
    });

    const { height: _, ...positionStyle } = style;

    return (
        <div ref={measureRef} style={{...positionStyle, overflow: 'visible'}}>
            <div
                className={`virtualized-member-item ${viewMode}-view ${isHighlighted ? 'member-highlighted' : ''}`}
                data-member-id={member.user_id}
            >
                <OptimizedMemberCard
                    key={member.user_id}
                    member={member}
                    viewMode={viewMode}
                    config={config}
                    onClick={() => handleMemberClick(member)}
                    isFavorite={checkIsFavorite}
                    onToggleFavorite={handleToggleFavorite}
                    isPendingFavorite={checkIsPending}
                    isLoggedIn={isLoggedIn}
                    data-member-id={member.user_id}
                />
            </div>
        </div>
    );
});

const MeasuredGridRowComp = memo(function MeasuredGridRowComp({ index, items, style, columns, setItemHeight }) {
    const measureRef = useRef(null);

    useEffect(() => {
        if (measureRef.current) {
            const height = measureRef.current.scrollHeight;
            setItemHeight(index, height);
        }
    });

    const { height: _, ...positionStyle } = style;

    return (
        <div ref={measureRef} style={{...positionStyle, overflow: 'visible'}} className="virtualized-grid-row" role="row">
            <div className="virtualized-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gridAutoRows: 'minmax(auto, max-content)' }}>
                {items}
            </div>
        </div>
    );
});

// Main row component used by react-window – receives all dynamic data via the `data` prop
const VirtualRow = memo(function VirtualRow({ index, style, data }) {
    const {
        sortedMembers, viewMode, highlightedMemberId,
        config, columns, handleMemberClick, checkIsFavorite,
        handleToggleFavorite, checkIsPending, isLoggedIn, setItemHeight
    } = data;

    if (viewMode === 'compact') {
        const member = sortedMembers[index];
        if (!member) return null;
        const isHighlighted = highlightedMemberId === member.user_id;

        return (
            <MeasuredCompactCard
                index={index}
                member={member}
                viewMode={viewMode}
                isHighlighted={isHighlighted}
                style={style}
                data={data}
            />
        );
    }

    // Cards view: render a grid row with N columns
    const startIndex = index * columns;
    const items = [];
    for (let col = 0; col < columns; col++) {
        const itemIndex = startIndex + col;
        const member = sortedMembers[itemIndex];
        if (!member) break;
        const isHighlighted = highlightedMemberId === member.user_id;
        items.push(
            <div
                key={member.user_id}
                className={`virtualized-member-item cards-view ${isHighlighted ? 'member-highlighted' : ''}`}
                data-member-id={member.user_id}
            >
                <OptimizedMemberCard
                    member={member}
                    viewMode={'cards'}
                    config={config}
                    onClick={() => handleMemberClick(member)}
                    isFavorite={checkIsFavorite}
                    onToggleFavorite={handleToggleFavorite}
                    isPendingFavorite={checkIsPending}
                    isLoggedIn={isLoggedIn}
                    data-member-id={member.user_id}
                />
            </div>
        );
    }

    return (
        <MeasuredGridRowComp
            index={index}
            items={items}
            style={style}
            columns={columns}
            setItemHeight={setItemHeight}
        />
    );
});

function VirtualizedMemberList({ 
    members, 
    loading, 
    config, 
    onMemberHover, 
    onMemberHoverOut, 
    registerMemberList,
    isFavorite,
    onToggleFavorite,
    isPendingFavorite,
    isLoggedIn = true,
    showResultCount = false,
    totalMembers,
    sortBy: externalSortBy,
    onSortChange,
    sortOptions = [],
    viewMode: externalViewMode,
    onViewModeChange
}) {
    const containerRef = useRef(null);
    const itemHeights = useRef(new Map());
    const resetPending = useRef(false);
    const resetMinIndex = useRef(Infinity);
    const [highlightedMemberId, setHighlightedMemberId] = useState(null);
    const { isMobile, isTablet, isDesktop } = useResponsive();

    // Determine columns based on viewport; target 3 columns on desktop
    const columns = useMemo(() => {
        if (isDesktop) return 3;
        if (isTablet) return 2;
        return 1;
    }, [isDesktop, isTablet]);

    // Use our optimization hooks
    const {
        sortBy,
        viewMode,
        sortedMembers,
        listRef,
        pinnedMemberId,
        setSortBy,
        setViewMode,
        scrollToMember,
        pinMember
    } = useVirtualizedList(members, [], {
        externalSortBy,
        onSortChange,
        sortOptions,
        externalViewMode,
        onViewModeChange
    });

    const {
        handleMemberHover,
        handleMemberHoverOut
    } = useOptimizedHover(onMemberHover, onMemberHoverOut, 150);

    const {
        checkIsFavorite,
        checkIsPending,
        handleToggleFavorite
    } = useOptimizedFavorites(isFavorite, isPendingFavorite, onToggleFavorite);

    // Register the list container with the parent
    useEffect(() => {
        if (registerMemberList && containerRef.current) {
            registerMemberList(containerRef.current);
        }
    }, [registerMemberList]);

    // Get item height for variable size list
    const getItemHeight = useCallback((index) => {
        const cached = itemHeights.current.get(index);
        if (cached) return cached;
        
        // Return estimated height based on view mode
        if (viewMode === 'compact') {
            return DEFAULT_COMPACT_HEIGHT;
        } else {
            // For cards view, we need to account for multiple cards per row
            // Use a larger estimate to prevent content cutoff at wider screens
            return DEFAULT_CARD_HEIGHT + 60; // Extra padding for row container and larger content
        }
    }, [viewMode]);

    // Set item height after render and batch react-window resets for performance
    const setItemHeight = useCallback((index, height) => {
        const currentHeight = itemHeights.current.get(index);
        if (currentHeight !== height) {
            itemHeights.current.set(index, height);
            resetMinIndex.current = Math.min(resetMinIndex.current, index);
            // Batch resets: schedule a single forced resetAfterIndex per animation frame
            if (!resetPending.current) {
                resetPending.current = true;
                requestAnimationFrame(() => {
                    if (listRef.current && typeof listRef.current.resetAfterIndex === 'function') {
                        listRef.current.resetAfterIndex(resetMinIndex.current, true);
                    }
                    resetPending.current = false;
                    resetMinIndex.current = Infinity;
                });
            }
        }
    }, []);

    // Handle member card click for highlighting
    const handleMemberClick = useCallback((member) => {
        setHighlightedMemberId(member.user_id);
        // Optional: also trigger the hover callback for map sync
        handleMemberHover(member);
    }, [handleMemberHover]);

    // Memoized data object passed to VirtualRow via react-window's itemData prop.
    // This keeps the VirtualRow component reference stable across re-renders,
    // preventing unmount/remount of rows (which caused images to reload).
    const itemData = useMemo(() => ({
        sortedMembers,
        viewMode,
        highlightedMemberId,
        config,
        columns,
        handleMemberClick,
        checkIsFavorite,
        handleToggleFavorite,
        checkIsPending,
        isLoggedIn,
        setItemHeight
    }), [sortedMembers, viewMode, highlightedMemberId, config, columns, handleMemberClick, checkIsFavorite, handleToggleFavorite, checkIsPending, isLoggedIn, setItemHeight]);

    // Track the actual members array (not sortedMembers which changes on pin)
    // to avoid full height-cache clears when only pinning changes the order.
    const prevMembersRef = useRef(members);
    const prevViewModeRef = useRef(viewMode);

    useEffect(() => {
        const membersChanged = prevMembersRef.current !== members;
        const viewModeChanged = prevViewModeRef.current !== viewMode;
        prevMembersRef.current = members;
        prevViewModeRef.current = viewMode;

        if (membersChanged || viewModeChanged) {
            itemHeights.current.clear();
            requestAnimationFrame(() => {
                if (listRef.current && typeof listRef.current.resetAfterIndex === 'function') {
                    listRef.current.resetAfterIndex(0, true);
                }
            });
        } else if (sortedMembers) {
            // Pinning only – reset index positions without clearing height cache
            requestAnimationFrame(() => {
                if (listRef.current && typeof listRef.current.resetAfterIndex === 'function') {
                    listRef.current.resetAfterIndex(0, false);
                }
            });
        }
    }, [viewMode, members, sortedMembers]);

    // Scroll to top when a member is pinned (after re-render places it at index 0)
    useEffect(() => {
        if (pinnedMemberId && listRef.current) {
            requestAnimationFrame(() => {
                if (listRef.current) {
                    listRef.current.scrollToItem(0, 'start');
                }
            });
        }
    }, [pinnedMemberId]);

    // Expose scroll method to parent
    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.scrollToMember = scrollToMember;
            // Expose additional methods for map sync
            containerRef.current.highlightMember = (memberId) => {
                // Pin the member to position #1, highlight it, and scroll to top
                pinMember(memberId);
                setHighlightedMemberId(memberId);
                return true;
            };
            containerRef.current.clearHighlight = () => {
                setHighlightedMemberId(null);
            };
            // Flag to indicate this is a virtualized list
            containerRef.current.isVirtualized = true;
        }
    }, [scrollToMember, sortedMembers, viewMode, columns, pinMember]);

    if (loading && members.length === 0) {
        return (
            <div className="member-list-container">
                <LoadingSpinner />
            </div>
        );
    }

    if (members.length === 0) {
        return (
            <div className="member-list-container">
                <div className="no-results">
                    <h3>No members found</h3>
                    <p>Try adjusting your search criteria or filters.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="member-list-container" ref={containerRef}>
            {!isMobile && (
                <div className="list-controls">
                    <div className="sort-controls">
                        <label htmlFor="sort-select">Sort by:</label>
                        <select
                            id="sort-select"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="sort-select"
                        >
                            {(sortOptions.length > 0 ? sortOptions : [
                                { value: 'joined', label: 'Recently Joined' },
                                { value: 'joined_asc', label: 'Oldest Joined' },
                                { value: 'name', label: 'Name (A-Z)' },
                                { value: 'name_desc', label: 'Name (Z-A)' }
                            ]).map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {showResultCount && (
                        <div className="result-count">
                            {totalMembers !== undefined ? 
                                `Displaying ${sortedMembers.length} of ${totalMembers} users found` :
                                `${sortedMembers.length} user${sortedMembers.length !== 1 ? 's' : ''} found`
                            }
                        </div>
                    )}

                    <div className="view-controls">
                        <button
                            onClick={() => setViewMode('cards')}
                            className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
                            aria-label="Card view"
                        >
                            Cards
                        </button>
                        <button
                            onClick={() => setViewMode('compact')}
                            className={`view-btn ${viewMode === 'compact' ? 'active' : ''}`}
                            aria-label="Compact view"
                        >
                            Compact
                        </button>
                    </div>
                </div>
            )}

            <div className="virtualized-list-container">
                <VariableSizeList
                    ref={listRef}
                    height={500} // Will be controlled by CSS
                    itemCount={viewMode === 'cards' ? Math.ceil(sortedMembers.length / columns) : sortedMembers.length}
                    itemSize={getItemHeight}
                    overscanCount={OVERSCAN_COUNT}
                    className={`virtualized-member-list ${viewMode}-view`}
                    itemData={itemData}
                >
                    {VirtualRow}
                </VariableSizeList>
            </div>

            {loading && (
                <div className="loading-more">
                    <LoadingSpinner size="small" />
                    Loading more members...
                </div>
            )}
        </div>
    );
}

export default VirtualizedMemberList;
